import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  benchmarkCaption,
  benchmarkTypicalLine,
  benchmarkYouLine,
  explanationBullets,
} from "@aucash/rules/au08";
import { AppBar, Button, Card, Chip, Eyebrow } from "../../src/components/brand";
import { Checkbox } from "../../src/components/controls";
import { SaveCard } from "../../src/components/SaveCard";
import { DisclaimerBlock } from "../../src/disclaimer";
import { FreePdfCard } from "../../src/components/PdfCard";
import { MARKETING_URL } from "../../src/env";
import { useAppObserva } from "../../src/observa/use-app-observa";
import { trpc } from "../../src/trpc";
import { aud, colors, fonts, radius, space, tabular, webNoOutline } from "../../src/theme";

type Au08Result = Awaited<ReturnType<typeof trpc.calc.getAssessment.query>>;

// Keep up to 2 decimals (MLS tiers are 1%, 1.25%, 1.5%) without trailing zeros.
const pct = (fraction: number): string => `${+(fraction * 100).toFixed(2)}%`;

export default function Result(): JSX.Element {
  const router = useRouter();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const id = Array.isArray(params.id) ? params.id[0] : params.id;

  const [result, setResult] = useState<Au08Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const observa = useAppObserva();

  useEffect(() => {
    if (id === undefined || id === "") return;
    let active = true;
    setResult(null);
    setError(null);
    trpc.calc.getAssessment
      .query({ id })
      .then((res) => {
        if (!active) return;
        setResult(res);
        // Mid-funnel: the result actually rendered (ran-and-saw-it).
        void observa?.event({
          eventType: "result.viewed",
          payload: { product: "AU08" },
        });
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Request failed");
      });
    return () => {
      active = false;
    };
  }, [id, observa]);

  if (id === undefined || id === "") {
    return (
      <View style={styles.root}>
        <StatusBar style="dark" />
        <View style={styles.centered}>
          <Text style={styles.errorTitle}>We couldn&apos;t find that result</Text>
          <Text style={styles.muted}>
            The link may be incomplete. Head back and enter your details again.
          </Text>
          <Button label="Back to calculator" onPress={() => router.replace("/au08")} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Private health · result" onRestart={() => router.replace("/au08")} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <Pressable onPress={() => router.replace("/au08")} accessibilityRole="button">
            <Text style={styles.backLink}>← Edit your details</Text>
          </Pressable>

          {/* §3.2: full disclaimer at the top, before any number. */}
          <DisclaimerBlock />

          {error !== null ? (
            <Card>
              <Text style={styles.errorTitle}>Couldn&apos;t reach the calculator</Text>
              <Text style={styles.muted}>{error}</Text>
              <Text style={styles.muted}>
                If you&apos;re running this locally, start the API with{" "}
                <Text style={styles.mono}>bun run dev:api</Text> (:7501).
              </Text>
            </Card>
          ) : result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : (
            <ResultBody result={result} id={id} />
          )}
        </View>
      </ScrollView>
    </View>
  );
}

function lhcCell(lhc: Au08Result["output"]["lhc"]): { value: string; note: string } {
  switch (lhc.status) {
    case "not_yet_applicable":
      return { value: "0%", note: "No loading yet — it starts after you turn 31." };
    case "no_loading":
      return { value: "None", note: "No Lifetime Health Cover loading — you got in early." };
    case "would_apply_if_taken_now":
      return {
        value: pct(lhc.loading),
        note: "The loading you'd pay on premiums if you take cover now. It rises 2% each year you wait.",
      };
    case "applies":
      return {
        value: pct(lhc.loading),
        note:
          lhc.yearsRemaining !== null
            ? `On your premium now — drops off after ${lhc.yearsRemaining} more year${lhc.yearsRemaining === 1 ? "" : "s"} of continuous cover.`
            : "On your premium — drops off after 10 continuous years of cover.",
      };
    case "indeterminate":
      return { value: "Check it", note: "Worth confirming with your fund whether a loading applies." };
  }
}

function ResultBody({
  result,
  id,
}: {
  result: Au08Result;
  id: string;
}): JSX.Element {
  const { output, input, ruleVersion, sources, sourcesVerifiedAt, benchmark } = result;
  const { mls, lhc } = output;
  // Pass the dependent-child count so the family-tier bullet can name the
  // per-child threshold effect (no-op for single filers / no kids).
  const bullets = explanationBullets(
    output,
    ruleVersion,
    sourcesVerifiedAt,
    input.children,
  );
  const apraSource =
    benchmark.sources.apraStatistics ?? Object.values(benchmark.sources)[0];

  const observa = useAppObserva();

  const owe = !mls.exemptViaCover && mls.amount > 0;
  const heroColor = owe ? colors.honeyDeep : colors.ocean;

  const lead = owe
    ? "For FY26, you're paying the Medicare Levy Surcharge of"
    : mls.exemptViaCover
      ? "You're covered — your Medicare Levy Surcharge is"
      : "Good news — for FY26 your Medicare Levy Surcharge is";

  const sub = owe
    ? `That's ${pct(mls.rate)} of ${aud(mls.testedIncome)} because you earn over the surcharge threshold and don't hold private hospital cover.`
    : mls.exemptViaCover
      ? "Because you hold private hospital cover, you're exempt from the surcharge this year."
      : "Your income is below the surcharge threshold, so the MLS doesn't apply to you this year. Worth a recheck if your income rises.";

  const mlsCell = mls.exemptViaCover
    ? { value: "Exempt", note: "You hold hospital cover" }
    : mls.amount > 0
      ? { value: aud(mls.amount), note: `${pct(mls.rate)} of your income` }
      : { value: "$0", note: "Under the threshold" };
  const lhcC = lhcCell(lhc);

  const steps = owe
    ? [
        "Private hospital cover removes the surcharge — even a basic policy qualifies.",
        "Cover held before 1 July reduces the surcharge for the days you're covered this year.",
        "Taking cover sooner also locks in a lower Lifetime Health Cover loading for life.",
      ]
    : mls.exemptViaCover
      ? [
          "You're sorted on the surcharge — no action needed.",
          "Recheck if your income or family situation changes mid-year.",
          lhc.status === "applies"
            ? "Ask your fund how many continuous years you've held cover — the loading drops off at 10."
            : "Keep your cover continuous to stay loading-free.",
        ]
      : [
          "Nothing to pay right now — but recheck if your income rises near the threshold.",
          "If you're near 31, weigh up cover soon to avoid the Lifetime Health Cover loading later.",
        ];

  return (
    <>
      <View style={styles.resHead}>
        <Eyebrow>Your result · FY26</Eyebrow>
        <Chip label={owe ? "● For your attention" : "✓ You're clear"} tone={owe ? "est" : "owed"} />
      </View>

      <Card style={styles.heroCard}>
        <Text style={styles.lead}>{lead}</Text>
        <Text testID="result-amount" style={[styles.heroBig, { color: heroColor }]}>
          {owe ? aud(mls.amount) : "$0"}
          {owe ? <Text style={styles.heroPer}> /yr</Text> : null}
        </Text>
        <Text style={styles.heroSub}>{sub}</Text>

        <View style={styles.split}>
          <View style={styles.cell}>
            <Text style={styles.cellKey}>Medicare Levy Surcharge</Text>
            <Text style={[styles.cellVal, { color: owe ? colors.honeyDeep : colors.ink }]}>
              {mlsCell.value}
            </Text>
            <Text style={styles.cellNote}>{mlsCell.note}</Text>
          </View>
          <View style={styles.cell}>
            <Text style={styles.cellKey}>Lifetime Health Cover</Text>
            <Text style={styles.cellVal}>{lhcC.value}</Text>
            <Text style={styles.cellNote}>{lhcC.note}</Text>
          </View>
        </View>
      </Card>

      <Section title="Why this is your number">
        {bullets.map((b, i) => (
          <View key={i} style={styles.whyRow}>
            <View style={styles.whyIcon}>
              <Text style={styles.whyIconText}>i</Text>
            </View>
            <Text style={styles.whyText}>{b}</Text>
          </View>
        ))}
      </Section>

      {/* §5.2 information-only peer context — both numbers, source stamped. */}
      <Section title="How you compare">
        <Text style={styles.compareText}>{benchmarkYouLine(benchmark)}</Text>
        <Text style={styles.compareText}>{benchmarkTypicalLine(benchmark)}</Text>
        <Text style={styles.verified}>{benchmarkCaption(benchmark)}</Text>
      </Section>

      <Section title="What you can do">
        {steps.map((c, i) => (
          <View key={i} style={styles.stepRow}>
            <View style={styles.stepNum}>
              <Text style={styles.stepNumText}>{i + 1}</Text>
            </View>
            <Text style={styles.whyText}>{c}</Text>
          </View>
        ))}
        {owe ? (
          <Text style={styles.affiliate}>
            If you switch via our compare page we may earn a referral fee — it
            never changes your price, and we list funds equally.{" "}
            <Text
              style={styles.affiliateLink}
              onPress={() => {
                const destination = `${MARKETING_URL}/compare`;
                // The affiliate hand-off — the revenue moment of the funnel.
                void observa?.event({
                  eventType: "compare.clicked",
                  payload: { product: "AU08", destination },
                });
                void Linking.openURL(destination);
              }}
            >
              Compare cover →
            </Text>
          </Text>
        ) : null}
      </Section>

      <Section title="Where these numbers come from">
        <SourceLink label="Medicare Levy Surcharge thresholds & rates — ATO" url={sources.mls} />
        <SourceLink label="Lifetime Health Cover loading — privatehealth.gov.au" url={sources.lhc} />
        <SourceLink label="Private hospital cover by age — APRA" url={apraSource} />
        <Text style={styles.verified}>
          Rules last verified {sourcesVerifiedAt} · ruleset {ruleVersion} ·
          benchmark {benchmark.sourcesVerifiedAt}
        </Text>
      </Section>

      <SaveCard id={id} />
      <FreePdfCard id={id} product="au08" />

      <Section title="Also worth checking">
        <CrossSellCard
          text="Got HECS debt? Check the marginal repayment for FY26 →"
          to="/au06"
        />
        <CrossSellCard
          text="Kids in care? See if the 3-Day Guarantee changed what you can claim →"
          to="/au13"
        />
      </Section>

      <EmailCaptureCard />
    </>
  );
}

// Post-result email capture (spec §5.1/§5.2). Information-only framing — we
// offer to flag when thresholds move, not "save $X". ACMA-compliant: consent
// box unchecked by default, submit disabled until ticked. Stores only the email.
function EmailCaptureCard(): JSX.Element {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return (
      <Card tone="sand">
        <Text style={styles.cardTitle}>You&apos;re on the list</Text>
        <Text style={styles.muted}>
          We&apos;ll email you if the thresholds change. Check your inbox for a
          welcome note — every email has a one-tap unsubscribe.
        </Text>
      </Card>
    );
  }

  const canSubmit = consent && email.trim().length > 0 && !submitting;

  const onSubmit = (): void => {
    if (!canSubmit) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setError(null);
    trpc.email.subscribe
      .mutate({ email: trimmed, consent: true, source: "au08-result" })
      .then(() => setDone(true))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Couldn't sign you up."),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <Card tone="sand">
      <Text style={styles.cardTitle}>Keep these numbers current</Text>
      <Text style={styles.muted}>
        ATO and APRA thresholds change every July. Get a short email when they
        do — no spam, just the update. We store only your email.
      </Text>
      <TextInput
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="email-address"
        textContentType="emailAddress"
        placeholder="you@example.com"
        placeholderTextColor={colors.muted}
        style={styles.input}
        accessibilityLabel="Email address for updates"
      />
      <Checkbox
        checked={consent}
        onChange={setConsent}
        label="Email me occasional updates from Money Back Australia. I can unsubscribe any time."
      />
      <Button
        label={submitting ? "Signing you up…" : "Email me updates"}
        onPress={onSubmit}
        disabled={!canSubmit}
      />
      {error !== null ? <Text style={styles.muted}>{error}</Text> : null}
    </Card>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({ label, url }: { label: string; url: string | undefined }): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

function CrossSellCard({ text, to }: { text: string; to?: string }): JSX.Element {
  const router = useRouter();
  if (to !== undefined) {
    return (
      <Pressable onPress={() => router.push(to)} accessibilityRole="button" style={styles.crossSell}>
        <Text style={styles.crossSellText}>{text}</Text>
        <View style={[styles.pill, styles.pillLive]}>
          <Text style={styles.pillTextLive}>Calculate →</Text>
        </View>
      </Pressable>
    );
  }
  return (
    <View style={styles.crossSell}>
      <Text style={styles.crossSellText}>{text}</Text>
      <View style={styles.pill}>
        <Text style={styles.pillText}>Coming soon</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 680, gap: space.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", gap: space.md, padding: space.xl },
  loading: { paddingVertical: space.xxl, alignItems: "center" },
  backLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },

  resHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },

  heroCard: { gap: space.sm },
  lead: { fontFamily: fonts.bodyMedium, fontSize: 16, color: colors.inkSoft },
  heroBig: {
    fontFamily: fonts.displayHeavy,
    fontSize: 64,
    letterSpacing: -2,
    lineHeight: 66,
    marginTop: space.xs,
    ...tabular,
  },
  heroPer: { fontFamily: fonts.bodySemibold, fontSize: 22, color: colors.muted },
  heroSub: { fontFamily: fonts.body, fontSize: 15, lineHeight: 22, color: colors.inkSoft, marginTop: space.xs },

  split: { flexDirection: "row", gap: space.md, marginTop: space.lg },
  cell: { flex: 1, backgroundColor: colors.sand, borderRadius: radius.md, padding: space.lg },
  cellKey: {
    fontFamily: fonts.mono,
    fontSize: 10.5,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.muted,
  },
  cellVal: { fontFamily: fonts.displayHeavy, fontSize: 24, color: colors.ink, marginTop: space.sm },
  cellNote: { fontFamily: fonts.body, fontSize: 12.5, lineHeight: 17, color: colors.inkSoft, marginTop: space.xs },

  section: { gap: space.md },
  sectionTitle: { fontFamily: fonts.display, fontSize: 20, letterSpacing: -0.4, color: colors.ink },

  whyRow: { flexDirection: "row", gap: space.md, alignItems: "flex-start" },
  whyIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.honeyTint,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  whyIconText: { fontFamily: fonts.displayHeavy, fontSize: 13, color: colors.honeyDeep },
  whyText: { flex: 1, fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  stepRow: { flexDirection: "row", gap: space.md, alignItems: "flex-start" },
  stepNum: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.honey,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  stepNumText: { fontFamily: fonts.displayHeavy, fontSize: 13, color: colors.ink },

  affiliate: { fontFamily: fonts.body, fontSize: 12.5, lineHeight: 18, color: colors.muted, marginTop: space.xs },
  affiliateLink: { fontFamily: fonts.bodySemibold, color: colors.oceanDeep },

  compareText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 21, color: colors.ink },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },

  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.oceanDeep },

  cardTitle: { fontFamily: fonts.display, fontSize: 18, color: colors.ink },
  promoNote: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.ocean },
  input: {
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radius.md,
    paddingVertical: 14,
    paddingHorizontal: space.lg,
    fontFamily: fonts.bodyMedium,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.card,
    ...webNoOutline,
  },

  crossSell: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.md,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
    backgroundColor: colors.card,
  },
  crossSellText: { flex: 1, fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.ink },
  pill: { backgroundColor: colors.sand, borderRadius: radius.sm, paddingHorizontal: space.sm, paddingVertical: space.xs },
  pillText: { fontFamily: fonts.bodySemibold, fontSize: 11, color: colors.muted },
  pillLive: { backgroundColor: colors.honey },
  pillTextLive: { fontFamily: fonts.bodyBold, fontSize: 11, color: colors.ink },

  errorTitle: { fontFamily: fonts.display, fontSize: 18, color: colors.danger },
  muted: { fontFamily: fonts.body, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
  mono: { fontFamily: fonts.mono },
});
